/**
 * Readable text for a brand colour.
 *
 * Mirrors the helper in user-frontend, which is what the customer app
 * actually renders with. It lives here too so the Branding preview tells the
 * truth: without it the preview paints white on whatever the partner picked,
 * so a partner choosing a light primary would be shown an illegible button
 * and told that is how their customers will see it. A preview that lies is
 * worse than no preview.
 */

const luminance = (hex: string): number | null => {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const channel = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return (
    0.2126 * channel(parseInt(h.slice(0, 2), 16)) +
    0.7152 * channel(parseInt(h.slice(2, 4), 16)) +
    0.0722 * channel(parseInt(h.slice(4, 6), 16))
  );
};

export const contrastRatio = (a: string, b: string): number | null => {
  const la = luminance(a);
  const lb = luminance(b);
  if (la === null || lb === null) return null;
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
};

export const WHITE = '#ffffff';
export const INK = '#111827';

/** Falls back to white for an unparseable colour, matching the stylesheet. */
export const readableOn = (background: string | null | undefined): string => {
  if (!background) return WHITE;
  const onWhite = contrastRatio(background, WHITE);
  const onInk = contrastRatio(background, INK);
  if (onWhite === null || onInk === null) return WHITE;
  return onWhite >= onInk ? WHITE : INK;
};
